'use client';
import { DetailedHTMLProps, HTMLAttributes, useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ScrollStateStore {
    states: Record<string, number>;
    setScroll: (key: string, scrollTop: number) => void;
    getScroll: (key: string) => number | undefined;
}

const useScrollStateStore = create<ScrollStateStore>()(
    persist(
        (set, get) => ({
            states: {},
            setScroll: (key, scrollTop) => {
                set((state) => ({
                    states: {
                        ...state.states,
                        [key]: scrollTop
                    }
                }));
            },
            getScroll: (key) => get().states[key]
        }),
        {
            name: 'scroll-state'
        }
    )
);

type onScrollCallback = HTMLAttributes<HTMLElement>['onScroll'];

const useRestoreScroll = <T extends HTMLElement>(
    key: string,
    props?: {
        onScroll?: onScrollCallback;
    }
): Pick<DetailedHTMLProps<HTMLAttributes<T>, T>, 'onScroll' | 'ref'> => {
    const ref = useRef<T>(null);
    const { getScroll, setScroll } = useScrollStateStore((state) => ({
        getScroll: state.getScroll,
        setScroll: state.setScroll
    }));

    const restoreScroll = useCallback(() => {
        const scrollTop = getScroll(key);
        if (ref.current && scrollTop !== undefined) {
            ref.current.scrollBy({
                top: scrollTop
            });
        }
    }, [key]);

    useEffect(() => {
        restoreScroll();

        if (window) {
            window.addEventListener('load', restoreScroll);
        }

        return () => {
            window.removeEventListener('load', restoreScroll);
        };
    }, [key]);

    const handleOnScroll = useCallback<onScrollCallback>(
        (event) => {
            const { scrollTop } = event.currentTarget;
            setScroll(key, scrollTop);
            props?.onScroll?.(event);
        },
        [key]
    );

    return { ref, onScroll: handleOnScroll };
};

export default useRestoreScroll;
